/**
 * LaunchController类 - 控制烟花发射的时序和数量
 * 
 * 职责:
 * - 响应UI发射指令
 * - 管理连发烟花的时序
 * - 随机化发射角度和位置
 * - 维护活跃烟花列表
 * 
 * 验证需求: Requirements 2.1, 2.2, 2.3, 2.4, 2.5
 */
class LaunchController {
  constructor(fireworkFactory) {
    if (!fireworkFactory) {
      throw new Error('FireworkFactory is required');
    }

    this.fireworkFactory = fireworkFactory;
    
    // 活跃烟花列表
    this.activeFireworks = [];
    
    // 连发队列
    this.burstQueue = [];
    this.burstTimer = 0;
    this.burstInterval = 0.25; // 连发间隔（秒）
  }

  /**
   * 发射单个烟花
   */
  launchSingle() {
    const firework = this.fireworkFactory.createFirework();
    firework.launch();
    this.activeFireworks.push(firework);
    
    return firework;
  }

  /**
   * 发射连发烟花
   * @param {number} count - 烟花数量
   * @param {number} interval - 发射间隔（秒），默认0.2-0.3秒
   */
  launchBurst(count, interval = null) {
    // 使用随机间隔（200-300ms）
    const burstInterval = interval || (0.2 + Math.random() * 0.1);
    
    // 创建连发队列
    for (let i = 0; i < count; i++) {
      this.burstQueue.push({
        delay: i * burstInterval,
        launched: false
      });
    }
    
    this.burstInterval = burstInterval;
    this.burstTimer = 0;
  }

  /**
   * 更新控制器状态
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    // 处理连发队列
    if (this.burstQueue.length > 0) {
      this.burstTimer += deltaTime;
      
      // 检查是否有烟花需要发射
      for (const item of this.burstQueue) {
        if (!item.launched && this.burstTimer >= item.delay) {
          this.launchSingle();
          item.launched = true;
        }
      }
      
      // 清理已发射的项
      this.burstQueue = this.burstQueue.filter(item => !item.launched);
      
      // 重置计时器
      if (this.burstQueue.length === 0) {
        this.burstTimer = 0;
      }
    }
    
    // 更新所有活跃烟花
    const finishedFireworks = [];
    
    for (const firework of this.activeFireworks) {
      firework.update(deltaTime);
      
      if (firework.isFinished()) {
        finishedFireworks.push(firework);
      }
    }
    
    // 清理完成的烟花
    for (const firework of finishedFireworks) {
      firework.dispose();
      const index = this.activeFireworks.indexOf(firework);
      if (index !== -1) {
        this.activeFireworks.splice(index, 1);
      }
    }
  }

  /**
   * 获取活跃烟花列表
   * @returns {Array}
   */
  getActiveFireworks() {
    return this.activeFireworks;
  }

  /**
   * 获取活跃烟花数量
   * @returns {number}
   */
  getActiveCount() {
    return this.activeFireworks.length;
  }

  /**
   * 清除所有烟花
   */
  clear() {
    // 清理所有活跃烟花
    for (const firework of this.activeFireworks) {
      firework.dispose();
    }
    
    this.activeFireworks = [];
    this.burstQueue = [];
    this.burstTimer = 0;
  }

  /**
   * 检查是否有连发正在进行
   * @returns {boolean}
   */
  isBurstActive() {
    return this.burstQueue.length > 0;
  }
}

export default LaunchController;
